import { useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

/* ─── Static data ─────────────────────────────────────────────────────────── */
const SHOWS = [
  { id: 'dkpol',  label: 'Dansk Politik',        emoji: '🗳️', color: '#FF6B35', share: 0.38 },
  { id: 'kaffe',  label: 'Kaffe & Kode',          emoji: '☕', color: '#7BC4E4', share: 0.30 },
  { id: 'storie', label: 'True Crime DK',          emoji: '🔍', color: '#B57BEE', share: 0.23 },
  { id: 'natur',  label: 'Natur & Nysgerrighed',  emoji: '🌿', color: '#4FD97F', share: 0.09 },
];

// Creator dashboard data — correct earnings formula: listeners × 24 kr × avg_pct
const PERIODS = [
  { id: '7d',  label: '7 dage',  earnings: '907,68',     listeners: '122',    avgPct: '31', perListener: '7,44' },
  { id: '30d', label: '30 dage', earnings: '13.970,88',  listeners: '2.431',  avgPct: '24', perListener: '5,75' },
  { id: '90d', label: '90 dage', earnings: '354.969,84', listeners: '32.194', avgPct: '46', perListener: '11,03' },
];

// Chart bar heights as % of max per period (12 bars)
const CHART_DATA = {
  '7d':  [30, 45, 60, 40, 80, 55, 70, 45, 90, 65, 75, 50],
  '30d': [40, 55, 70, 60, 85, 75, 80, 65, 90, 70, 85, 95],
  '90d': [55, 65, 75, 70, 80, 85, 90, 75, 95, 80, 92, 88],
};

const MONTH = new Intl.DateTimeFormat('da-DK', { month: 'long', year: 'numeric' }).format(new Date());

/* ─── Kaffeindekset donut data ────────────────────────────────────────────── */
const KAFFESHOWS = [
  { label: 'Kaffe & Kode',          pct: 38, kr: '9,12',  color: '#FF6B35' },
  { label: 'Dansk Geopolitik',      pct: 27, kr: '6,48',  color: '#FF9163' },
  { label: 'True Crime DK',         pct: 23, kr: '5,52',  color: '#E55520' },
  { label: 'Natur & Nysgerrighed',  pct: 12, kr: '2,88',  color: '#FFB590' },
];

/* ─── SVG Donut ───────────────────────────────────────────────────────────── */
const DONUT_SIZE   = 140;
const DONUT_R      = 52;
const DONUT_SW     = 16;
const DONUT_CX     = DONUT_SIZE / 2;
const DONUT_CIRC   = 2 * Math.PI * DONUT_R;
const GAP_DEG      = 3;

function buildSegments(items) {
  let cumDeg = -90;
  return items.map(item => {
    const arcDeg  = item.pct * 3.6;
    const dashLen = Math.max(0, (arcDeg - GAP_DEG) / 360 * DONUT_CIRC);
    const rotate  = cumDeg;
    cumDeg += arcDeg;
    return { ...item, dashLen, rotate };
  });
}

function DonutChart({ items }) {
  const segments = buildSegments(items);
  return (
    <svg width={DONUT_SIZE} height={DONUT_SIZE} viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}>
      {/* track */}
      <circle cx={DONUT_CX} cy={DONUT_CX} r={DONUT_R}
        fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={DONUT_SW} />
      {segments.map((seg, i) => (
        <circle key={i}
          cx={DONUT_CX} cy={DONUT_CX} r={DONUT_R}
          fill="none"
          stroke={seg.color}
          strokeWidth={DONUT_SW}
          strokeLinecap="butt"
          strokeDasharray={`${seg.dashLen} ${DONUT_CIRC - seg.dashLen}`}
          transform={`rotate(${seg.rotate} ${DONUT_CX} ${DONUT_CX})`}
        />
      ))}
      {/* centre label */}
      <text x={DONUT_CX} y={DONUT_CX - 7}
        textAnchor="middle" dominantBaseline="middle"
        fill="#E5E2E1" fontSize="11" fontWeight="700"
        fontFamily="JetBrains Mono, monospace">
        24,00 kr
      </text>
      <text x={DONUT_CX} y={DONUT_CX + 10}
        textAnchor="middle" dominantBaseline="middle"
        fill="rgba(229,226,225,0.5)" fontSize="9"
        fontFamily="Manrope, sans-serif">
        denne måned
      </text>
    </svg>
  );
}

/* ─── Component ───────────────────────────────────────────────────────────── */
function Home() {
  const [activePeriod, setActivePeriod] = useState('30d');

  const period    = PERIODS.find(p => p.id === activePeriod);
  const chartBars = CHART_DATA[activePeriod];

  return (
    <div>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="fc-hero">
        <div className="fc-wrap">
          <div className="fc-hero-grid">

            {/* Copy */}
            <div className="fc-hero-copy">
              <div className="fc-hero-badge">
                <span className="fc-hero-badge-dot" />
                Dansk podcast-platform
              </div>

              <h1 className="fc-hero-title">
                Støt dem<br />
                du <em>lytter til.</em>
              </h1>

              <p className="fc-hero-sub">
                30 kr om måneden. 80% går direkte til de podcasts du lytter til — fordelt efter din faktiske lyttetid. Ingen annoncer. Ingen tricks.
              </p>

              <div className="fc-hero-actions">
                <Link to="/subscribe" className="fc-cta-primary">
                  Kom i gang for 30 kr/md
                </Link>
                <Link to="/for-creators" className="fc-cta-ghost">
                  For skabere →
                </Link>
              </div>
            </div>

            {/* Receipt */}
            <div className="fc-receipt-wrap">
              <div className="fc-receipt">
                <div className="fc-receipt-header">
                  <span className="fc-receipt-logo">Faircast</span>
                  <span className="fc-receipt-date">{MONTH}</span>
                </div>

                <hr className="fc-receipt-divider" />

                {SHOWS.map(s => {
                  const kr = (30 * 0.80 * s.share).toFixed(2);
                  return (
                    <div className="fc-receipt-row" key={s.id}>
                      <div className="fc-receipt-row-label">
                        <span className="fc-receipt-dot" style={{ background: s.color }} />
                        <span>{s.label}</span>
                      </div>
                      <div className="fc-receipt-bar-wrap">
                        <div
                          className="fc-receipt-bar"
                          style={{ width: `${s.share * 100}%`, background: s.color }}
                        />
                      </div>
                      <span className="fc-receipt-amount">{kr} kr</span>
                    </div>
                  );
                })}

                <hr className="fc-receipt-divider" />

                <div className="fc-receipt-total-row">
                  <span className="fc-receipt-total-label">Til skaberne i alt</span>
                  <span className="fc-receipt-total-value">24,00 kr</span>
                </div>

                <div className="fc-receipt-platforms">
                  Faircast drift: 6,00 kr — det er alt vi tager
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Allocator ─────────────────────────────────────────────────────── */}
      <section className="fc-allocator fc-section">
        <div className="fc-wrap">
          <div className="fc-alloc-grid">

            <div className="fc-alloc-copy">
              <span className="fc-eyebrow">Transparens</span>
              <h2 className="fc-section-title">
                Dine 30 kr fordeles efter hvad du faktisk lytter til
              </h2>
              <p className="fc-section-sub">
                Ingen flade betalinger. Ingen mystiske algoritmer. Skaberne får præcis den andel af din tid, de fortjener — og du kan se regnestykket.
              </p>
            </div>

            <div className="fc-alloc-visual">
              <div className="fc-alloc-bar-wrap">
                <div className="fc-alloc-track">
                  <div className="fc-alloc-fill" style={{ width: '80%' }} />
                </div>
                <div className="fc-alloc-labels">
                  <span>0 kr</span>
                  <span style={{ color: 'var(--primary-color)', fontWeight: 700 }}>80% til skabere</span>
                  <span>30 kr</span>
                </div>
              </div>

              <div className="fc-alloc-rows">
                {SHOWS.map(s => {
                  const kr = (30 * 0.80 * s.share).toFixed(2);
                  return (
                    <div className="fc-alloc-row" key={s.id}>
                      <span className="fc-alloc-show">{s.emoji} {s.label}</span>
                      <div className="fc-alloc-mini-track">
                        <div
                          className="fc-alloc-mini-fill"
                          style={{ width: `${s.share * 100}%`, background: s.color }}
                        />
                      </div>
                      <span className="fc-alloc-kr">{kr} kr</span>
                    </div>
                  );
                })}
              </div>

              <p className="fc-alloc-note">
                Beregnet ud fra din lyttetid denne måned. Opdateres dagligt.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── Kaffeindekset ─────────────────────────────────────────────────── */}
      <section className="fc-section" style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="fc-wrap">
          <div className="fc-grid-2 fc-grid-2--center">

            {/* Copy */}
            <div>
              <span className="fc-eyebrow">Kaffeindekset</span>
              <h2 className="fc-section-title">
                Billigere end en<br />McDonald's-kaffe
              </h2>
              <p className="fc-section-sub">
                En stor kaffe på McDonald's koster 35 kr. Et Faircast-abonnement koster 30 kr — og 80% af dem går direkte til de podcasts du elsker.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--text-secondary)', marginTop: 16 }}>
                Hvert sekund du lytter, optjener podcasten en lille smule af dine penge. Pauser du, stopper tællingen. Alt opgøres sidst på måneden, så din lyttetid bliver til det, dine favoritter modtager. Det er fair — for begge parter.
              </p>
            </div>

            {/* Phone mockup */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: 260,
                background: '#0F0F0F',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 28,
                padding: '28px 20px 24px',
                boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              }}>
                {/* Screen header */}
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: 'rgba(229,226,225,0.5)', marginBottom: 2 }}>Din støtte denne måned</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#E5E2E1' }}>april 2026</div>
                </div>

                {/* Donut */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                  <DonutChart items={KAFFESHOWS} />
                </div>

                {/* Podcast rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {KAFFESHOWS.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: s.color, flexShrink: 0,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#E5E2E1', lineHeight: 1.2 }}>{s.label}</div>
                        <div style={{
                          height: 3, background: 'rgba(255,255,255,0.08)',
                          borderRadius: 2, marginTop: 4,
                        }}>
                          <div style={{
                            height: '100%', width: `${s.pct}%`,
                            background: s.color, borderRadius: 2,
                          }} />
                        </div>
                      </div>
                      <div style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11, fontWeight: 700, color: '#E5E2E1',
                        flexShrink: 0,
                      }}>{s.kr} kr</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Creator section ───────────────────────────────────────────────── */}
      <section className="fc-creator-section fc-section" id="creators">
        <div className="fc-wrap">
          <div className="fc-creator-grid">

            {/* Copy */}
            <div>
              <span className="fc-eyebrow">For skabere</span>
              <h2 className="fc-section-title">
                80% af pengene. Altid.
              </h2>
              <p className="fc-section-sub">
                Vi tager 20% til drift — det er vores eneste forretningsmodel. Ingen annoncer der trumfer dit indhold. Ingen "premium tier" der halverer din andel.
              </p>

              <div style={{ marginTop: 32 }}>
                <Link to="/for-creators" className="fc-cta-primary">
                  Se hvad skabere tjener →
                </Link>
              </div>
            </div>

            {/* Creator dashboard mockup */}
            <div className="fc-creator-dash">
              <div className="fc-dash-header">
                <span className="fc-dash-title">Dit overblik</span>
                <div className="fc-period-tabs">
                  {PERIODS.map(p => (
                    <button
                      key={p.id}
                      className={`fc-period-tab${activePeriod === p.id ? ' active' : ''}`}
                      onClick={() => setActivePeriod(p.id)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="fc-dash-kpis">
                <div className="fc-kpi">
                  <div className="fc-kpi-label">Optjent</div>
                  <div className="fc-kpi-value fc-kpi-value--coral">{period.earnings} kr</div>
                  <div className="fc-kpi-sub">efter 80/20</div>
                </div>
                <div className="fc-kpi">
                  <div className="fc-kpi-label">Lyttere</div>
                  <div className="fc-kpi-value">{period.listeners}</div>
                  <div className="fc-kpi-sub">unikke denne periode</div>
                </div>
                <div className="fc-kpi">
                  <div className="fc-kpi-label">Lytteandel</div>
                  <div className="fc-kpi-value">{period.avgPct} %</div>
                  <div className="fc-kpi-sub">gennemsnit</div>
                </div>
                <div className="fc-kpi">
                  <div className="fc-kpi-label">Pr. lytter</div>
                  <div className="fc-kpi-value fc-kpi-value--coral">{period.perListener} kr</div>
                  <div className="fc-kpi-sub">denne periode</div>
                </div>
              </div>

              <div className="fc-dash-chart">
                <div className="fc-chart-bars">
                  {chartBars.map((h, i) => (
                    <div
                      key={i}
                      className={`fc-chart-bar${i === chartBars.length - 1 ? ' active' : ''}`}
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="fc-chart-labels">
                  <span>jan</span>
                  <span>apr</span>
                  <span>jul</span>
                  <span>okt</span>
                  <span>i dag</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Promises ──────────────────────────────────────────────────────── */}
      <section className="fc-promises fc-section" id="promises">
        <div className="fc-wrap">
          <h2 className="fc-promises-title">Tre løfter. Ingen undtagelser.</h2>
          <p className="fc-promises-sub">
            Vi skriver det ned, fordi ord uden regnskab ikke er noget værd.
          </p>

          <div className="fc-promises-grid">
            <div className="fc-promise-card">
              <div className="fc-promise-icon">📊</div>
              <h3 className="fc-promise-title">
                <span className="fc-promise-number">80%</span>
                Til skaberne — altid
              </h3>
              <p className="fc-promise-body">
                80 øre af hver krone du betaler går direkte til de podcasts du lytter til. Det er ikke marketing — det er regnskab. Du kan se det selv.
              </p>
            </div>

            <div className="fc-promise-card">
              <div className="fc-promise-icon">📖</div>
              <h3 className="fc-promise-title">
                <span className="fc-promise-number">0 kr</span>
                Skjulte gebyrer
              </h3>
              <p className="fc-promise-body">
                30 kr om måneden. Det er det hele. Ingen transaktionsgebyr, ingen "service fee", ingen stigning du ikke hørte om.
              </p>
            </div>

            <div className="fc-promise-card">
              <div className="fc-promise-icon">🚫</div>
              <h3 className="fc-promise-title">
                <span className="fc-promise-number">0</span>
                Annoncer. Nogensinde.
              </h3>
              <p className="fc-promise-body">
                Vi sælger ikke din opmærksomhed til annoncører. Vores eneste forretning er abonnementet — ikke dine data, ikke din adfærd, ikke din tid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="fc-final-cta fc-section--tight">
        <div className="fc-wrap">
          <div className="fc-cta-inner">
            <h2 className="fc-cta-title">
              Klar til et <em>fair</em> abonnement?
            </h2>
            <p className="fc-cta-sub">
              30 kr om måneden. Opsig når som helst. Intet der binder dig — kun god dansk podcast.
            </p>

            <div className="fc-cta-actions">
              <Link to="/subscribe" className="fc-cta-primary">
                Kom i gang for 30 kr/md
              </Link>
              <Link to="/for-creators" className="fc-cta-ghost">
                Jeg er skaber →
              </Link>
            </div>

            <p className="fc-cta-note">Ingen binding · Opsig når som helst · 80% til skaberne</p>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;
